import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import { MinerControlAction3 } from "@mobx/api/miner";
import { isObjectResponse } from "@services/rackService";
import { EQUIPMENT_TYPE, IWorkEquipment } from "@store/Equipments/actionTypes";
import { entries, fromEntries } from "@utils/entries";
import { Edge, Node } from "reactflow";
import {
  IBuilding,
  IRack,
  IRackItem3,
  IRackItemForRack3,
  RACK_SECTION_TYPE3,
  TypePartialRackItem3,
  isMinerItem3,
  toRackItem3,
} from "../locations/types";

export const CURRENT_SHEME_VERSION3 = "260323";

export type PlaceID3 = number; // building or location
export type NodeFigure3 = "square" | "triangle" | "circle" | "diamond";
export type NodeType3 =
  | "cell"
  | "transformer"
  | "counter"
  | "shield"
  | "consumer"
  | "network"
  | NodeFigure3;

export const nodeTypeColorMap3: { [k in NodeType3]: string } = {
  cell: "#DFF9E8",
  transformer: "#FBEDFF",
  counter: "#DCEAFF",
  shield: "#F3E8E2",
  consumer: "#FFFEE2",
  network: "#DFF9E8",
  square: "#DFF9E8",
  triangle: "#FBEDFF",
  circle: "#DCEAFF",
  diamond: "#F3E8E2",
};

export const getNodeTypeColor3 = (type: NodeType3) => nodeTypeColorMap3[type];

export enum NodeSignal3 {
  STOP = 0,
  START,
  RESET,
}

export interface BuildingData3 extends IBuilding {
  miners: IRackItem3[];
}

export interface RackSectionData3 {
  rackID: number;
  rackName: string;
  rackDisplayName?: string;
  rackItemID?: number;
  sectionKey: string;
  x: number;
  y: number;
  pos: RACK_SECTION_TYPE3;
  miner?: IRackItemForRack3;
}

export const isRackSectionData3 = (data: unknown): data is RackSectionData3 =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as RackSectionData3).rackID === "number" &&
  typeof (data as RackSectionData3).sectionKey === "string" &&
  typeof (data as RackSectionData3).rackName === "string";

export const rackSectionKey3 = (rackID: number, x: number, y: number) => `${rackID}:${x}:${y}`;

export const formatRackDisplayName3 = (
  locationCode?: string,
  buildingName?: string,
  rackName?: string
) => {
  const parts = [locationCode, buildingName, rackName]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part);
  return parts.join("-");
};

export const toRackSectionData3 = (
  rack: IRack,
  item: TypePartialRackItem3,
  rackDisplayName?: string
): RackSectionData3 => ({
  rackID: rack.id,
  rackName: rack.rack_id,
  rackDisplayName,
  rackItemID: item.id,
  sectionKey: rackSectionKey3(rack.id, item.x, item.y),
  x: item.x,
  y: item.y,
  pos: item.pos as RACK_SECTION_TYPE3,
  miner: isMinerItem3(item) ? { ...toRackItem3(item), rackID: rack.id } : undefined,
});

export const getRackSectionState3 = (data: RackSectionData3): NodeState3 =>
  data.miner?.status ?? { on: 0, off: 0 };

export const findRackSectionInRack3 = (
  rack: IRack,
  section: Pick<RackSectionData3, "rackItemID" | "x" | "y">
): TypePartialRackItem3 | undefined =>
  rack.rack_items.find((item) =>
    (section.rackItemID !== undefined && item.id === section.rackItemID) ||
    (item.x === section.x && item.y === section.y)
  );

export const isBuildingData3 = (data: unknown): data is BuildingData3 =>
  typeof data === "object" &&
  data !== null &&
  isObjectResponse(data) &&
  (data as unknown as BuildingData3).miners !== undefined;

export const signalControl3: { [k in NodeSignal3]: MinerControlAction3 } = {
  [NodeSignal3.START]: "start",
  [NodeSignal3.STOP]: "stop",
  [NodeSignal3.RESET]: "restart",
} as const;

export const DEFAULT_KIND3 = EQUIPMENT_TYPE.ELECTRICAL;

export const editorNameFromID3 = (kind: EQUIPMENT_TYPE, id?: PlaceID3): string | undefined =>
  id === undefined ? undefined : `${kind}::${id}`;

export const editorName3 = (locationID: string | number, buildingID?: string): string | undefined => {
  if (locationID === undefined) return undefined;

  let locID: number;
  if (typeof locationID === "number") locID = locationID;
  else locID = +locationID;

  let buildID: number | undefined;
  if (typeof buildingID !== "undefined") {
    buildID = +buildingID;
  }
  return `${locID}::${buildID}`;
};

const STATES_ON3: ReadonlySet<MINER_STATUSES_E> = new Set([
  MINER_STATUSES_E.OK,
  MINER_STATUSES_E.LOW,
  MINER_STATUSES_E.BAD,
]);

export type NodeComplexState3 = { on: number; off: number };
export type NodeState3 = NodeComplexState3 | MINER_STATUSES_E;

export interface ConsumersDropMeta3 {
  createdNodeIDs: string[];
  duplicateNodeIDs: string[];
}

export interface CreateConsumersResult3 {
  data: EditorData3;
  meta: ConsumersDropMeta3;
}

export interface CreateConsumersActionResult3 {
  savedHint?: boolean;
  meta: ConsumersDropMeta3;
}

export const isStateOn3 = (state: NodeState3) =>
  typeof state === "number" ? STATES_ON3.has(state) : state.on > 0;

export const isSignalAllowed3 = (state: NodeState3, signal: NodeSignal3) =>
  typeof state === "number"
    ? (signal === NodeSignal3.START) === !isStateOn3(state)
    : signal === NodeSignal3.START
    ? state.off > 0
    : state.on > 0;

export const getCollectionState3 = (miners: TypePartialRackItem3[]): NodeComplexState3 => {
  const result = { on: 0, off: 0 };
  for (const item of miners) {
    if (!isMinerItem3(item)) continue;
    if (isStateOn3(item.status)) result.on++;
    else result.off++;
  }
  return result;
};

export type ConsumerKind3 = "rack" | "miner" | "section" | "building";

export interface NodeData3 {
  kind: EQUIPMENT_TYPE;
  shemeKind: EQUIPMENT_TYPE;
  ID: number;
  placeID: PlaceID3;
  type: NodeType3;
  name: string;
  state: NodeState3;
  info?: string;
  color?: string;
  userData?: unknown;
  consumer?: ConsumerKind3;
  highlighted?: boolean;
  lastSignal?: { ts: number; signal: NodeSignal3; order: number; active: boolean; success?: boolean };
  needConfirm?: NodeState3;
}

export const toWorkEquipment3 = (data: NodeData3): IWorkEquipment => ({
  id: `#${data.ID}`,
  name: data.name,
  type: data.kind,
  info: data.info,
  color: data.color ?? "rgba(0,0,0,0)",
  num: data.ID,
  on: isStateOn3(data.state),
  subtype: data.type,
});

export type ShemeNode3 = Node<NodeData3, NodeType3>;
export type ShemeEdge3 = Edge<any>;

export type NodesCounter3 = {
  [k in NodeType3]: number;
};

export interface NodeTree3 {
  forth: { [nodeID: string]: Set<string> | undefined };
}

export interface SerializedNodeTree3 {
  forth: { [nodeID: string]: string[] | undefined };
}

export const addArc3 = (tree: NodeTree3, from: string, to: string): NodeTree3 => {
  if (!Object.hasOwn(tree.forth, from) || tree.forth[from] === undefined) {
    tree.forth = { ...tree.forth, [from]: new Set([to]) };
  } else {
    tree.forth = { ...tree.forth, [from]: new Set([...tree.forth[from]!, to]) };
  }
  return { ...tree };
};

export const removeArc3 = (tree: NodeTree3, to: string): NodeTree3 => {
  const pairs: [string, Set<string> | undefined][] = Object.entries(tree.forth);
  return {
    forth: Object.fromEntries(
      pairs.map(([id, t]) => {
        if (!t) return [id, t];
        t.delete(to);
        return [id, t];
      })
    ),
  };
};

export const removeAllArcs3 = (tree: NodeTree3, node: string): NodeTree3 => {
  delete tree.forth?.[node];
  return removeArc3(tree, node);
};

export interface EditorData3 {
  lastNodeID: number;
  nodesCounter: NodesCounter3;
  nodes: ShemeNode3[];
  edges: Edge<any>[];
  tree: NodeTree3;
}

export const isLegacyMinerConsumer3 = (node: ShemeNode3) =>
  node.type === "consumer" && node.data.consumer === "miner";

export const isValidElectricalScheme3 = (data: EditorData3) =>
  data.nodes.every((node) => !isLegacyMinerConsumer3(node));

export interface SerializedEditorData3 {
  lastNodeID: number;
  nodesCounter: NodesCounter3;
  nodes: ShemeNode3[];
  edges: Edge<any>[];
  tree: SerializedNodeTree3;
}

export interface PlaceEditorsData3 {
  version: string;
  saved: boolean;
  kinds: { [kind in EQUIPMENT_TYPE]: EditorData3 };
}

export interface SerializedPlaceEditorsData3 {
  version: string;
  saved: boolean;
  kinds: { [kind in EQUIPMENT_TYPE]: SerializedEditorData3 };
}

export interface DragInfo3 {
  kind: ConsumerKind3;
  id: string;
}

export interface DraggedElementData3 extends DragInfo3 {
  data: unknown;
}

export interface DraggedElementInfo3 extends DraggedElementData3 {
  dragID: string;
}

export const toSerializedNodeTree3 = (tree: NodeTree3): SerializedNodeTree3 => ({
  forth: fromEntries(entries(tree.forth).map(([k, v]) => [k, v ? [...v] : v])),
});

export const toNodeTree3 = (tree: SerializedNodeTree3): NodeTree3 => ({
  forth: fromEntries(entries(tree.forth).map(([k, v]) => [k, v ? new Set([...v]) : v])),
});
